import {
  Component,
  ElementRef,
  OnDestroy,
  ViewChild,
  inject,
  input,
  output,
  signal
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { StripeElements, StripePaymentElement } from '@stripe/stripe-js';
import { StripeService } from '../../../../core/services/stripe/stripe.service';
import { UserService } from '../../../../core/services/user.service';
import { StripeMessagingConstants } from '../../../../core/constants/messaging.constants';
import { PaymentMethodType } from '../../../../core/enums/data.enums';

@Component({
  selector: 'ms-stripe-add-credit-card',
  standalone: true,
  imports: [CommonModule, FontAwesomeModule],
  templateUrl: './stripe-add-credit-card.component.html'
})
export class StripeAddCreditCardComponent implements OnDestroy {
  @ViewChild('paymentElementContainer') paymentElementContainer?: ElementRef<HTMLDivElement>;

  private stripeService = inject(StripeService);
  private userService = inject(UserService);

  public userId = input.required<number>();

  public paymentMethodSaved = output<void>();
  public cancelled = output<void>();

  public isLoading = signal(true);
  public isSaving = signal(false);
  public errorMessage = signal<string | null>(null);
  public isPaymentElementReady = signal(false);

  private elements: StripeElements | null = null;
  private paymentElement: StripePaymentElement | null = null;

  public async initialize(): Promise<void> {
    this.isLoading.set(true);
    this.errorMessage.set(null);

    try {
      const setupIntentResult = await this.userService.createSetupIntent(this.userId(), PaymentMethodType.Card);

      if (!setupIntentResult.success || !setupIntentResult.data?.clientSecret) {
        this.errorMessage.set(StripeMessagingConstants.STRIPE_ERROR_CREATING_SETUP_INTENT());
        return;
      }

      const elementsResult = await this.stripeService.createElement({
        clientSecret: setupIntentResult.data.clientSecret
      });

      if (!elementsResult.success || !elementsResult.data) {
        this.errorMessage.set(StripeMessagingConstants.STRIPE_ERROR_CREATING_SETUP_INTENT());
        return;
      }

      this.elements = elementsResult.data;

      setTimeout(() => {
        this.mountPaymentElement();
      }, 0);
    } catch {
      this.errorMessage.set(StripeMessagingConstants.STRIPE_ERROR_CREATING_SETUP_INTENT());
    } finally {
      this.isLoading.set(false);
    }
  }

  private mountPaymentElement(): void {
    if (!this.elements || !this.paymentElementContainer) {
      return;
    }

    this.paymentElement = this.elements.create('payment', {
      paymentMethodOrder: ['card'],
      wallets: {
        link: 'never'
      }
    });
    this.paymentElement.on('ready', () => {
      this.isPaymentElementReady.set(true);
    });
    this.paymentElement.mount(this.paymentElementContainer.nativeElement);
  }

  public async savePaymentMethod(): Promise<void> {
    if (!this.elements || this.isSaving()) {
      return;
    }

    this.isSaving.set(true);
    this.errorMessage.set(null);

    try {
      const stripe = await this.stripeService.getStripeInstance();

      if (!stripe) {
        this.errorMessage.set(StripeMessagingConstants.STRIPE_ERROR_ADDING_PAYMENT_METHOD());
        return;
      }

      const { error } = await stripe.confirmSetup({
        elements: this.elements,
        confirmParams: {
          return_url: window.location.href
        },
        redirect: 'if_required'
      });

      if (error) {
        if (error.type !== 'validation_error') {
          this.errorMessage.set(error.message || StripeMessagingConstants.STRIPE_ERROR_ADDING_PAYMENT_METHOD());
        }
      } else {
        this.paymentMethodSaved.emit();
        this.cleanup();
      }
    } catch {
      this.errorMessage.set(StripeMessagingConstants.STRIPE_ERROR_ADDING_PAYMENT_METHOD());
    } finally {
      this.isSaving.set(false);
    }
  }

  public onCancel(): void {
    this.cleanup();
    this.cancelled.emit();
  }

  private cleanup(): void {
    if (this.paymentElement) {
      this.paymentElement.destroy();
      this.paymentElement = null;
    }
    this.elements = null;
    this.isPaymentElementReady.set(false);
  }

  ngOnDestroy(): void {
    this.cleanup();
  }
}
